/**
 * location-validation — the app's single door onto the location field rules.
 *
 * Authored 2026-08-07 (Nick) for the Locations admin + bulk-import surface
 * (`Locations - Admin & Bulk Creation.dc.html`). The RULES themselves are NOT
 * here — they live in the contract (`@v0y4ge/api-contracts` →
 * `address-formats.ts` + `validateLocationDraft`), because the serving engine
 * and, in production, the real backend enforce the same ones. This module is
 * the UI-side adapter over them:
 *
 *   · `addressRows()`   — the contract's field specs grouped into render rows,
 *   · `validate()`      — the contract validator plus localization,
 *   · `parseDelimited()`/`autoMapColumns()` — importer plumbing.
 *
 * Why the editor and the importer share it: a row an administrator can type by
 * hand but not import (or the reverse) is a bug the user experiences as the
 * product contradicting itself.
 *
 * i18n note (2026-08-07): the plain, un-parameterized messages are wired
 * through `t(key, fallback)` here and self-heal when the keys land. The
 * parameterized ones ("ZIP code doesn't match the United States format —
 * expected 12345 or 12345-6789") are ICU by nature, and CLAUDE.md's i18n rule
 * forbids shipping an ICU call site before its key exists — so those keep the
 * contract's English until REQ-133's `locations.validation.*` batch ships.
 */
import {
  addressFormatFor,
  resolveCountry,
  validateLocationDraft,
  normalizeLocationDraft,
  isValidTimeZone,
  SUPPORTED_ADDRESS_COUNTRIES,
  ADDRESS_FORMATS,
  type AddressFormat,
  type AddressFieldSpec,
} from "@v0y4ge/api-contracts";
import { t as i18nT } from "../../lib/i18n";

export type { AddressFormat, AddressFieldSpec };
export { addressFormatFor, resolveCountry, normalizeLocationDraft, isValidTimeZone, SUPPORTED_ADDRESS_COUNTRIES, ADDRESS_FORMATS };

/** Every field the editor and the importer can hold a value (and an error) for. */
export type LocField =
  | "name" | "address" | "addressLine2" | "city" | "region" | "postalCode"
  | "country" | "phone" | "timezone" | "externalCode" | "hours";

export type LocErrors = Partial<Record<LocField, string>>;

/** One window in the weekly grid. */
export interface HoursWindow { open: string; close: string }
export type WeeklyHours = { day: string; windows: HoursWindow[] }[];

export interface LocFormDraft {
  id?: string;
  name: string;
  address: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  phone: string;
  timezone: string;
  externalCode: string;
  hours: WeeklyHours;
  hoursSet: boolean;
  active: boolean;
  archived?: boolean;
  /** WGS-84 coordinates for the map preview; absent = never located. */
  latitude?: number;
  longitude?: number;
  [key: string]: unknown;
}

export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export const WEEKDAY_LABEL: Record<string, string> = {
  mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun",
};

export function emptyDraft(partial: Partial<LocFormDraft> = {}): LocFormDraft {
  return {
    name: "", address: "", addressLine2: "", city: "", region: "", postalCode: "",
    country: "", phone: "", timezone: "", externalCode: "",
    hours: WEEKDAYS.map((d) => ({ day: d, windows: [] })),
    hoursSet: false, active: true,
    ...partial,
  };
}

/**
 * The country's field specs, grouped into render rows. Fields sharing a `row`
 * sit side by side, in the order the country writes them — which is the whole
 * point: Japan's postal code leads, Germany's PLZ precedes the Ort.
 */
export function addressRows(country: string | null | undefined): AddressFieldSpec[][] {
  const format = addressFormatFor(country);
  const byRow = new Map<number, AddressFieldSpec[]>();
  for (const spec of format.fields) {
    const bucket = byRow.get(spec.row);
    if (bucket) bucket.push(spec);
    else byRow.set(spec.row, [spec]);
  }
  return [...byRow.keys()].sort((a, b) => a - b).map((k) => byRow.get(k) as AddressFieldSpec[]);
}

/** Country options for the picker, named and sorted. */
export function countryOptions(): { code: string; name: string }[] {
  return SUPPORTED_ADDRESS_COUNTRIES
    .map((code) => ({ code, name: ADDRESS_FORMATS[code].name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Localize the messages that are plain strings; leave the parameterized ones
 * as the contract wrote them (see the i18n note at the top of this file).
 */
function localize(field: string, message: string): string {
  switch (message) {
    case "Enter a location name.":
      return i18nT("locations.validation.nameRequired", "Enter a location name.");
    case "Choose a country — it decides the rest of the address form.":
      return i18nT("locations.validation.countryRequired", "Choose a country — it decides the rest of the address form.");
    case "Set a time zone — staff signing in here read every time against it.":
      return i18nT("locations.validation.timeZoneRequired", "Set a time zone — staff signing in here read every time against it.");
    case "Enter a valid phone number — 7–15 digits, optional leading +.":
      return i18nT("locations.validation.phoneFormat", "Enter a valid phone number — 7–15 digits, optional leading +.");
    default:
      void field;
      return message;
  }
}

export interface ValidateOpts {
  /** Validate only these fields (blur validates one at a time). */
  only?: readonly LocField[];
  /** Store codes held by OTHER locations. */
  takenExternalCodes?: readonly string[];
  requireTimeZone?: boolean;
}

/** Run the contract validator over a draft and localize what can be localized. */
export function validate(draft: Partial<LocFormDraft>, opts: ValidateOpts = {}): LocErrors {
  const raw = validateLocationDraft(draft as Record<string, unknown>, {
    only: opts.only,
    takenExternalCodes: opts.takenExternalCodes,
    requireTimeZone: opts.requireTimeZone !== false,
  });
  const out: LocErrors = {};
  for (const [field, message] of Object.entries(raw)) {
    out[field as LocField] = localize(field, message);
  }
  return out;
}

export function hasErrors(errors: LocErrors): boolean {
  return Object.keys(errors).length > 0;
}

/* ── Importer plumbing ──────────────────────────────────────────────────── */

/** Every column an import can target, in the order the template writes them. */
export const IMPORT_FIELDS: { key: LocField; label: string; labelKey: string }[] = [
  { key: "name", label: "Name", labelKey: "locations.importField.name.label" },
  { key: "externalCode", label: "Store code", labelKey: "locations.importField.externalCode.label" },
  { key: "address", label: "Street address", labelKey: "locations.importField.address.label" },
  { key: "addressLine2", label: "Address line 2", labelKey: "locations.importField.addressLine2.label" },
  { key: "city", label: "City", labelKey: "locations.importField.city.label" },
  { key: "region", label: "State / region", labelKey: "locations.importField.region.label" },
  { key: "postalCode", label: "Postal code", labelKey: "locations.importField.postalCode.label" },
  { key: "country", label: "Country", labelKey: "locations.importField.country.label" },
  { key: "phone", label: "Phone", labelKey: "locations.importField.phone.label" },
  { key: "timezone", label: "Time zone", labelKey: "locations.importField.timezone.label" },
];

/**
 * Split delimited text into a header row + data rows. Handles quoted fields,
 * embedded commas and newlines, and auto-detects comma vs tab — a paste out of
 * Excel or Sheets is tab-separated, and refusing it would make "paste from a
 * spreadsheet" a lie.
 */
export function parseDelimited(text: string): { headers: string[]; rows: string[][] } {
  const src = String(text ?? "").replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!src.trim()) return { headers: [], rows: [] };

  const firstLine = src.slice(0, src.indexOf("\n") === -1 ? src.length : src.indexOf("\n"));
  const delim = (firstLine.match(/\t/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? "\t" : ",";

  const all: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') { quoted = true; continue; }
    if (ch === delim) { row.push(cell); cell = ""; continue; }
    if (ch === "\n") { row.push(cell); all.push(row); row = []; cell = ""; continue; }
    cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c !== "")) all.push(row);
  if (!all.length) return { headers: [], rows: [] };

  const headers = (all[0] ?? []).map((h) => h.trim());
  const rows = all.slice(1).filter((r) => r.some((c) => String(c).trim() !== ""));
  return { headers, rows };
}

/** Header synonyms an exported file actually uses, per target field. */
const HEADER_HINTS: Record<LocField, string[]> = {
  name: ["name", "location", "locationname", "branch", "branchname", "site", "store", "storename", "title"],
  externalCode: ["storecode", "code", "legacyid", "externalid", "externalcode", "branchno", "branchnumber", "siteid", "costcentre", "costcenter"],
  address: ["address", "address1", "addr", "addr1", "addressline1", "street", "streetaddress", "line1", "logradouro", "strasse", "straße"],
  addressLine2: ["address2", "addr2", "addressline2", "line2", "suite", "unit", "floor", "locality", "adresszusatz", "complemento", "building"],
  city: ["city", "town", "townorcity", "suburb", "ort", "commune", "plaats", "cidade", "ward"],
  region: ["state", "province", "region", "county", "prefecture", "stateprov", "stateprovince", "bundesland", "kanton", "canton", "estado"],
  postalCode: ["zip", "zipcode", "postal", "postalcode", "postcode", "plz", "cep", "pin", "pincode", "codepostal"],
  country: ["country", "countrycode", "iso", "iso2", "countryiso", "nation"],
  phone: ["phone", "telephone", "tel", "phonenumber", "contact", "contactnumber"],
  timezone: ["timezone", "tz", "ianatz", "zone", "timezoneid"],
  hours: [],
};

const squash = (s: string) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Best-effort header → field matching. Exact synonym first, then a contains
 * match, and every field is claimed at most once so two columns never fight
 * over `city`. Returns null for anything it can't place — the operator maps
 * those in the grid header, which is why guessing wrong is cheap.
 */
export function autoMapColumns(headers: string[]): (LocField | null)[] {
  const taken = new Set<LocField>();
  const exact = (h: string): LocField | null => {
    const s = squash(h);
    for (const { key } of IMPORT_FIELDS) {
      if (taken.has(key)) continue;
      if (HEADER_HINTS[key].includes(s)) return key;
    }
    return null;
  };
  const fuzzy = (h: string): LocField | null => {
    const s = squash(h);
    if (!s) return null;
    for (const { key } of IMPORT_FIELDS) {
      if (taken.has(key)) continue;
      if (HEADER_HINTS[key].some((hint) => hint.length >= 3 && (s.includes(hint) || hint.includes(s)))) return key;
    }
    return null;
  };
  const pass1 = headers.map((h) => {
    const hit = exact(h);
    if (hit) taken.add(hit);
    return hit;
  });
  return pass1.map((hit, i) => {
    if (hit) return hit;
    const f = fuzzy(headers[i] ?? "");
    if (f) taken.add(f);
    return f;
  });
}

/** The downloadable starter file — headers only, in template order. */
export function templateCsv(): string {
  return IMPORT_FIELDS.map((f) => f.label).join(",") + "\r\n";
}

/** Trigger a browser download of `text` as `filename`. */
export function downloadText(filename: string, text: string, mime = "text/csv;charset=utf-8"): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
